import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { CredentialService } from "src/app/services/core/credential/credential.service";
import { SignupService } from "src/app/services/core/signup/signup.service";
import { NUMBER_OF_RECORDS } from "src/app/utils/common.utils";
import { sweetAlert } from "src/app/utils/swal";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { sortBy } from "lodash";
import { firstValueFrom } from "rxjs";
import { CompanyService } from "src/app/services/core/company/company.service";

@UntilDestroy()
@Component({
  selector: "app-companies-list",
  templateUrl: "./companies-list.component.html",
  styleUrls: ["./companies-list.component.css"],
})
export class CompaniesListComponent implements OnInit {
  users: any = [];
  search: any;
  companyDetail: any;
  companies: any = [];
  currentPage = 1;
  totalPages = 0;
  totalResults = 0;
  numberOfRecords = NUMBER_OF_RECORDS;
  selectedDropdownId: any;
  toggleActionButtons = false;
  syncingId: any = null;
  destroyedRef = inject(DestroyRef);

  constructor(
    private companyService: CompanyService,
    private credentialService: CredentialService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.getCompanies();
    window.addEventListener("message", this.onQuickBooksMessage);
  }

  ngOnDestroy() {
    window.removeEventListener("message", this.onQuickBooksMessage);
  }

  onQuickBooksMessage = (event: MessageEvent) => {
    if (event.data?.type !== "QB_COMPANY_CONNECTED") {
      return;
    }
    if (
      event.origin !== window.location.origin &&
      event.data.status !== "success" &&
      event.data.status !== "error"
    ) {
      return;
    }
    this.getCompanies();
  };

  opendetailModal(item: any) {
    this.companyDetail = item;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    // this.getUsers(page);
  }

  getCompanies() {
    this.companyService.getCompanies().subscribe((data: any) => {
      if (data) {
        this.companies = data.results;
      }
    });
  }

  attachCompnay(id: any) {
    this.companyService
      .attachCompnay(id, { returnUrl: window.location.origin })
      .subscribe(
        (data: any) => {
          if (data) {
            window.open(data.authUrl, "qb-attach-company");
          }
        },
        (err) => {
          sweetAlert("Error", err, "error", "Ok");
        },
      );
  }

  async deAttachCompnay(company: any) {
    const result = await sweetAlert(
      "Alert",
      `Detach ${company?.CompanyName || "this company"} from QuickBooks?`,
      "warning",
      "Yes",
      "No",
    );
    if (!result?.isConfirmed && !result?.value) {
      return;
    }

    this.closeActionMenu(false);
    this.companyService.deAttachCompnay(company.id).subscribe({
      next: (data: any) => {
        this.getCompanies();
        sweetAlert(
          "Success",
          data?.message || "Company detached successfully.",
          "success",
          "Ok",
        );
      },
      error: (err: any) => {
        sweetAlert("Error", err, "error", "Ok");
      },
    });
  }

  onSyncData(id: any, type: any) {
    if (this.syncingId) {
      return;
    }
    this.syncingId = id;
    const request =
      type === "companies"
        ? this.companyService.syncCompanyById(id, type)
        : this.companyService.syncAllData(id, type);

    this.companyService.syncAllData(id, type).subscribe({
      next: (data: any) => {
        console.log("data", data);
        this.toggleActionButtons = false;
      },
      error: (err: any) => {
        console.error(err);
      },
      complete: () => {
        this.closeActionMenu(false);
      },
    });
    request.subscribe({
      next: (data: any) => {
        this.getCompanies();
        sweetAlert(
          "Success",
          data?.message || "Synced successfully",
          "success",
          "Ok",
        );
      },
      error: (err: any) => {
        sweetAlert("Error", err, "error", "Ok");
      },
      complete: () => {
        this.syncingId = null;
        this.closeActionMenu(false);
      },
    });
  }

  async onSyncAll(company: any) {
    if (this.syncingId) {
      return;
    }
    const result = await sweetAlert(
      "Alert",
      `Sync all QuickBooks data for ${company?.CompanyName || "this company"}?`,
      "warning",
      "Yes",
      "No",
    );
    if (!result?.isConfirmed && !result?.value) {
      return;
    }

    this.syncingId = company.id;
    this.closeActionMenu(false);

    const jobs = [
      { type: "companies", label: "Company" },
      { type: "customers", label: "Customers" },
      { type: "employees", label: "Employees" },
      { type: "vendors", label: "Vendors" },
      { type: "services", label: "Services" },
      { type: "invoices", label: "Invoices" },
      { type: "bills", label: "Bills" },
    ];
    const failures: string[] = [];

    try {
      for (const job of jobs) {
        try {
          const request =
            job.type === "companies"
              ? this.companyService.syncCompanyById(company.id, job.type)
              : this.companyService.syncAllData(company.id, job.type);
          await firstValueFrom(request);
        } catch (err: any) {
          failures.push(`${job.label}: ${err}`);
        }
      }

      this.getCompanies();
      if (failures.length) {
        sweetAlert("Error", failures.join(" | "), "error", "Ok");
        return;
      }
      sweetAlert(
        "Success",
        "All company data synced successfully.",
        "success",
        "Ok",
      );
    } finally {
      this.syncingId = null;
    }
  }

  showActionButtons(id: any) {
    this.selectedDropdownId = id;
    this.toggleActionButtons = true;
  }

  closeActionMenu(evt: any) {
    this.selectedDropdownId = null;
    this.toggleActionButtons = false;
  }
}
